<ng-container *ngFor="let metric of metrics; trackBy: trackById">
  <ng-container
    *ngTemplateOutlet="metric.tool === 'call_plan' ? callPlanTemplate : shouldShowSkeleton(metric) ? skeletonTemplate : metricTemplate; context: { metric: metric }"></ng-container>
</ng-container>

<!-- Skeleton Template -->
<ng-template #skeletonTemplate>
  <div class="metric-tile loading">
    <div class="metric-tile-content">
      <ion-skeleton-text class="skl skeleton-text-animated label"></ion-skeleton-text>
      <ion-skeleton-text class="skl skeleton-text-animated value"></ion-skeleton-text>
      <ion-skeleton-text class="skl skeleton-text-animated subtext"></ion-skeleton-text>
    </div>
    <ion-skeleton-text class="skl skeleton-text-animated progress"></ion-skeleton-text>
  </div>
</ng-template>

<!-- Actual Metric Template -->
<ng-template #metricTemplate let-metric="metric">
  <div
    class="metric-tile"
    (click)="openTool(metric)"
    [class.selected]="metric.isSelected"
    [class.disabled]="isMetricDisabled(metric)"
    [class.refreshing]="metric.isRefreshing">
    <div class="metric-tile-content">
      <div class="label">{{ metric.label }}</div>
      <div class="value">{{ metric.stringValue }}</div>
      <div class="label">{{ metric.subtext }}</div>
    </div>
    <ng-container [ngSwitch]="metric.tool">
      <ng-container *ngSwitchCase="'opportunity'" [ngTemplateOutlet]="progressCountChart"
        [ngTemplateOutletContext]="{metric: metric}"></ng-container>
      <ng-container *ngSwitchCase="MeasureType.meeting" [ngTemplateOutlet]="lineChart"
        [ngTemplateOutletContext]="{metric: metric}"></ng-container>
      <ng-container *ngSwitchCase="MeasureType.message" [ngTemplateOutlet]="barGraph"
        [ngTemplateOutletContext]="{metric: metric}"></ng-container>
      <!-- Coaching KPI temporarily hidden from the homepage metrics. -->
      <!-- <ng-container *ngSwitchCase="MeasureType.coaching" [ngTemplateOutlet]="barGraph"
        [ngTemplateOutletContext]="{metric: metric}"></ng-container> -->
      <ng-container *ngSwitchCase="MeasureType.procedure" [ngTemplateOutlet]="lineChart"
        [ngTemplateOutletContext]="{metric: metric}"></ng-container>
    </ng-container>
    <div class="metric-refresh-overlay" *ngIf="metric.isRefreshing">
      <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle class="metric-loader-dot metric-loader-dot-grey metric-loader-dot-grey-back" cx="10" cy="15" r="2.5" />
        <circle class="metric-loader-dot metric-loader-dot-blue" cx="20" cy="15" r="2.5" />
        <circle class="metric-loader-dot metric-loader-dot-grey metric-loader-dot-grey-front" cx="10" cy="15" r="2.5" />
      </svg>
    </div>
  </div>
</ng-template>

<ng-template #callPlanTemplate let-metric="metric">
  <div *ngIf="shouldShowSkeleton(metric); else callPlanTile" class="metric-tile call-plan-tile wide loading"
    data-testid="home-call-plan-tile-skeleton">
    <div class="metric-tile-content call-plan-headline">
      <ion-skeleton-text class="skl skeleton-text-animated label"></ion-skeleton-text>
      <ion-skeleton-text class="skl skeleton-text-animated value"></ion-skeleton-text>
      <ion-skeleton-text class="skl skeleton-text-animated label"></ion-skeleton-text>
    </div>
    <div class="call-plan-stat" *ngFor="let stat of [1, 2, 3]">
      <ion-skeleton-text class="skl skeleton-text-animated stat-value"></ion-skeleton-text>
      <ion-skeleton-text class="skl skeleton-text-animated stat-label"></ion-skeleton-text>
    </div>
    <ion-skeleton-text class="skl skeleton-text-animated progress"></ion-skeleton-text>
  </div>
  <ng-template #callPlanTile>
    <div class="metric-tile call-plan-tile call-plan-grid" [class.wide]="metric.count > 0 && metric.callPlanSummary"
      [class.selected]="metric.isSelected" (click)="openTool(metric)" data-testid="home-call-plan-tile">
      <div class="label headline title" data-testid="home-call-plan-tile-label">{{ metric.label }}</div>
      <div class="value headline" [class.empty]="!metric.count" data-testid="home-call-plan-tile-value">
        {{ metric.count ? metric.stringValue : '—' }}</div>
      <div class="label headline stat-label" [class.days-left]="metric.count && metric.callPlanSummary?.daysLeft >= 0"
        data-testid="home-call-plan-tile-subtext">
        {{ !metric.count ? ('NO_CALL_PLANS' | translate)
          : metric.callPlanSummary?.daysLeft >= 0 ? ('SPOTLIGHT_DAYS_LEFT' | translate: { days: metric.callPlanSummary.daysLeft })
          : metric.subtext }}</div>
      <ng-container *ngIf="metric.count > 0 && metric.callPlanSummary as summary">
        <div class="stat-divider goal"></div>
        <div class="stat-value goal" data-testid="home-call-plan-tile-goal">
          {{ summary.goalCompletion }}<span>/{{ summary.totalGoal }}</span></div>
        <div class="label stat-label goal" data-testid="home-call-plan-tile-goal-label">{{ 'GOAL' | translate }}</div>
        <div class="stat-divider reach"></div>
        <div class="stat-value reach" data-testid="home-call-plan-tile-reach">
          {{ summary.reach }}<span>/{{ summary.totalReach }}</span></div>
        <div class="label stat-label reach" data-testid="home-call-plan-tile-reach-label">{{ 'SPOTLIGHT_REACH' | translate }}</div>
        <div class="stat-divider frequency"></div>
        <div class="stat-value frequency" [title]="summary.avgFrequencyText" data-testid="home-call-plan-tile-avg-frequency">
          {{ summary.avgFrequency }}</div>
        <div class="label stat-label frequency" data-testid="home-call-plan-tile-avg-frequency-label">
          {{ 'SPOTLIGHT_AVG_FREQUENCY' | translate }}</div>
      </ng-container>
      <circular-chart [value]="metric.value" class="progress circular-chart" data-testid="home-call-plan-tile-ring" />
    </div>
  </ng-template>
</ng-template>

<!-- Tool-specific templates -->
<ng-template #progressCountChart let-metric="metric">
  <div class="progress progress-count-container">
    <div class="progress-count">{{ metric.count }}</div>
    <div class="progress-label">{{ 'OPEN' | translate }}</div>
  </div>
</ng-template>

<ng-template #lineChart let-metric="metric">
  <div class="progress line-chart">
    <line-graph [data]="metric.data | map: 'value1'"/>
    <div class="chart-labels">
      <div class="chart-label" *ngFor="let value of metric.data; trackBy: trackById">{{ value.label }}</div>
    </div>
  </div>
</ng-template>

<ng-template #barGraph let-metric="metric">
  <div class="progress bar-graph">
    <bar-graph [data]="metric.data"/>
    <div class="chart-labels">
      <div class="chart-label" *ngFor="let value of metric.data; trackBy: trackById">{{ value.label }}</div>
    </div>
  </div>
</ng-template>

<ng-template #areaGraph let-metric="metric">
  <area-graph class="progress area-graph" [data]="metric.data | map: 'value1'" [width]="48" [height]="48" />
</ng-template>
